import { api } from "../Assets/api/api";

// AI-driven natural language parser for attendance commands.
// Now uses backend API endpoint instead of direct AI API calls.

// Command types for attendance
const COMMAND_TYPES = {
  PRESENT: "present",
  ABSENT: "absent",
  LATE: "late",
  PRESENT_ALL: "presentAll",
  ABSENT_ALL: "absentAll",
  LATE_ALL: "lateAll",
  PRESENT_EXCEPT: "presentExcept",
  ABSENT_EXCEPT: "absentExcept",
  LATE_EXCEPT: "lateExcept",
  MULTIPLE_STUDENTS: "multipleStudents", // New type for multiple students
  NAVIGATE_LESSON: "navigateLesson", // New type for lesson navigation
  NEXT_LESSON: "nextLesson",
  PREVIOUS_LESSON: "previousLesson",
  GO_TO_LESSON: "goToLesson",
  ADD_NOTE: "addNote", // New type for adding student notes
  UPDATE_NOTE: "updateNote", // New type for updating student notes
  CLEAR_NOTE: "clearNote", // New type for clearing student notes
  MULTIPLE_COMMANDS: "multipleCommands" // New type for multiple different commands
};

// Enhanced student name matching utilities
const normalizeForMatching = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' ') // Normalize spaces
    .replace(/\b(student|estudiante)\b/g, '') // Remove "student" keywords
    .trim();
};

const calculateSimilarity = (str1, str2) => {
  const s1 = normalizeForMatching(str1);
  const s2 = normalizeForMatching(str2);
  
  if (s1 === s2) return 1.0;
  if (!s1 || !s2) return 0.0;
  
  // Levenshtein distance based similarity
  const matrix = [];
  const len1 = s1.length;
  const len2 = s2.length;
  
  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  const distance = matrix[len2][len1];
  const maxLen = Math.max(len1, len2);
  return maxLen === 0 ? 1.0 : (maxLen - distance) / maxLen;
};

const matchStudentByNumberToken = (textWithDigits, studentList) => {
  if (!textWithDigits || !studentList?.length) return null;
  const numberTokens = String(textWithDigits)
    .toLowerCase()
    .match(/\b\d+\b/g);
  if (!numberTokens?.length) return null;

  const sortedTokens = [...numberTokens].sort((a, b) => b.length - a.length);

  for (const token of sortedTokens) {
    const stripped = token.replace(/^0+/, "") || token;
    for (const student of studentList) {
      const normalizedName = normalizeForMatching(student.StudentName);
      let normalizedEmail = normalizeForMatching(student.StudentEmailId);
      // Also consider email local-part versions with hyphen/underscore removed
      if (student.StudentEmailId) {
        const [local] = String(student.StudentEmailId).split("@");
        normalizedEmail += ` ${normalizeForMatching(local)} ${local.replace(/[\W_]+/g, "")}`;
      }
      if (
        normalizedName.includes(token) ||
        normalizedName.includes(stripped) ||
        normalizedEmail.includes(token) ||
        normalizedEmail.includes(stripped)
      ) {
        return {
          student,
          confidence: 0.85,
          matchedField: "numberToken",
        };
      }
    }
  }
  return null;
};

const findBestStudentMatch = (spokenName, studentList, threshold = 0.6) => {
  if (!spokenName || !studentList?.length) return null;
  
  let bestMatch = null;
  let bestScore = 0;
  
  // Handle ordinal references like "student 2", "second student", etc.
  const extractIndexFromName = (nameLike) => {
    if (!nameLike) return null;
    const lower = String(nameLike).toLowerCase();
    // If the phrase contains "student 01" pattern, treat it as a literal tag rather than ordinal
    if (/student\s+0\d+/i.test(lower)) {
      return null;
    }
    const wordToNum = {
      one: 1, two: 2, three: 3, four: 4, five: 5,
      six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
      first: 1, second: 2, third: 3, fourth: 4, fifth: 5,
      sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10,
    };
    const digitMatch = lower.match(/\b(\d{1,2})\b/);
    if (digitMatch) return parseInt(digitMatch[1], 10);
    for (const [word, num] of Object.entries(wordToNum)) {
      if (lower.includes(word)) return num;
    }
    return null;
  };

  const getStudentByIndexFromName = (nameLike) => {
    const idx1 = extractIndexFromName(nameLike);
    if (!idx1 || idx1 < 1 || idx1 > studentList.length) return null;
    return studentList[idx1 - 1];
  };

  // First try direct name/email matching (covers names with numbers like "Student 01")
  for (const student of studentList) {
    const nameScore = calculateSimilarity(spokenName, student.StudentName);
    const emailScore = calculateSimilarity(spokenName, student.StudentEmailId);
    const maxScore = Math.max(nameScore, emailScore);
    
    if (maxScore > bestScore && maxScore >= threshold) {
      bestScore = maxScore;
      bestMatch = {
        student,
        confidence: maxScore,
        matchedField: nameScore >= emailScore ? 'name' : 'email'
      };
    }
  }

  if (bestMatch) {
    return bestMatch;
  }

  const byNumberToken = matchStudentByNumberToken(spokenName, studentList);
  if (byNumberToken) {
    return byNumberToken;
  }

  // Fallback to ordinal/index reference if no direct match found
  const byIndex = getStudentByIndexFromName(spokenName);
  if (byIndex) {
    return {
      student: byIndex,
      confidence: 0.9,
      matchedField: 'index'
    };
  }
  
  return null;
};

const sanitizeSlug = (value) => {
  if (typeof value !== "string") return "";
  let sanitized = value.trim();
  if (!sanitized) return "";

  const stripSlugPrefix = (str) => str.replace(/^\?*slug=/i, "");

  sanitized = stripSlugPrefix(sanitized);
  try {
    sanitized = decodeURIComponent(sanitized);
  } catch (_err) {
    // ignore decode errors, keep original
  }
  sanitized = stripSlugPrefix(sanitized);
  sanitized = sanitized.replace(/\//g, "");
  return sanitized;
};

// Helper function to get slug from common sources
function getSlug(slugOverride = null) {
  if (slugOverride) {
    return sanitizeSlug(slugOverride);
  }

  if (typeof window !== "undefined") {
    const urlSlug =
      new URLSearchParams(window.location.search).get("slug") || "";
    if (urlSlug) {
      return sanitizeSlug(urlSlug);
    }

    const storedSlug = window.localStorage?.getItem("slug");
    if (storedSlug) {
      return sanitizeSlug(storedSlug);
    }

    const userStr = window.localStorage?.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user?.slug) {
          return sanitizeSlug(user.slug);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }

  return "";
}

export async function parseAttendanceCommandWithAI(
  commandText,
  studentList = [],
  slug = null,
) {
  const prompt = String(commandText || "").trim();
  if (!prompt) return null;

  try {
    // Use provided slug or try to get it from common sources
    const finalSlug = getSlug(slug);
    const slugParam = finalSlug ? `?slug=${encodeURIComponent(finalSlug)}` : "";

    const response = await api.post(
      `/api/gpt/parse-attendance-command${slugParam}`,
      {
        commandText: prompt,
        studentList: studentList,
      }
    );

    // Log token usage on frontend
    if (response.data?.tokenUsage) {
      const { promptTokens, completionTokens, totalTokens } = response.data.tokenUsage;
      console.log('📊 [Frontend] parseAttendanceCommand Token Usage:', {
        endpoint: 'parse-attendance-command',
        promptTokens,
        completionTokens,
        totalTokens,
        commandText: prompt.substring(0, 50) + (prompt.length > 50 ? '...' : ''),
        studentCount: studentList.length,
      });
    }

    if (response.data?.status !== "Success" || !response.data?.data) {
      return null;
    }

    const parsed = response.data.data;
    
    // Debug logging for note extraction
    if (parsed?.type === 'addNote' || parsed?.type === 'updateNote' || parsed?.type === 'clearNote') {
      console.log('🔍 [aiCommandParser] Note command parsed:', {
        type: parsed.type,
        studentName: parsed.studentName,
        noteText: parsed.noteText,
        fullParsed: parsed
      });
    }
    
    if (!parsed) {
      return null;
    }

    // Handle multiple students case
    if (parsed?.type === "multipleStudents" && Array.isArray(parsed?.students)) {
      const result = {
        type: COMMAND_TYPES.MULTIPLE_STUDENTS,
        originalCommand: prompt,
        multipleStudents: []
      };

      // Process each student in the array
      for (const studentInfo of parsed.students) {
        const studentName = studentInfo.name;
        const status = studentInfo.status;
        
        if (studentName && status && studentList?.length > 0) {
          const bestMatch = findBestStudentMatch(studentName, studentList);
          
          if (bestMatch) {
            result.multipleStudents.push({
              studentName: bestMatch.student.StudentName,
              type: status, // Use the status as the type (present, absent, late)
              matchedStudent: bestMatch.student,
              confidence: bestMatch.confidence
            });
          } else {
            // Include even if no match found
            result.multipleStudents.push({
              studentName: studentName,
              type: status,
              matchedStudent: null,
              confidence: 0
            });
          }
        }
      }
      
      return result;
    } 
    // Handle multiple commands case
    else if (parsed?.type === "multipleCommands" && Array.isArray(parsed?.commands)) {
      const result = {
        type: COMMAND_TYPES.MULTIPLE_COMMANDS,
        originalCommand: prompt,
        multipleCommands: []
      };

      // Process each command in the array
      for (const commandInfo of parsed.commands) {
        const commandType = commandInfo.type;
        const studentName = commandInfo.studentName;
        const noteText = commandInfo.noteText;
        
        if (commandType && studentName) {
          // Find the best student match for each command
          const bestMatch = studentList?.length > 0 ? findBestStudentMatch(studentName, studentList) : null;
          
          result.multipleCommands.push({
            type: commandType,
            studentName: bestMatch ? bestMatch.student.StudentName : studentName,
            noteText: noteText || null,
            matchedStudent: bestMatch ? bestMatch.student : null,
            confidence: bestMatch ? bestMatch.confidence : 0
          });
        }
      }
      
      return result;
    }
    // Handle single student case (backward compatibility)
    else {
      const result = {
        type: parsed?.type ?? null,
        studentName: parsed?.studentName ?? null,
        reason: parsed?.reason ?? null,
        lessonName: parsed?.lessonName ?? null,
        noteText: parsed?.noteText ?? null, // New field for student notes
        originalCommand: prompt,
        confidence: null,
        matchedStudent: null
      };

      // If we have a student name from AI and a student list, try to find the best match
      if (studentList?.length > 0) {
        let bestMatch = null;
        if (result.studentName) {
          bestMatch = findBestStudentMatch(result.studentName, studentList);
        }

        const tokenMatchFromPrompt = matchStudentByNumberToken(prompt, studentList);

        if (!bestMatch && tokenMatchFromPrompt) {
          bestMatch = tokenMatchFromPrompt;
        } else if (
          tokenMatchFromPrompt &&
          tokenMatchFromPrompt.student?.StudentName &&
          tokenMatchFromPrompt.student.StudentName !== bestMatch?.student?.StudentName
        ) {
          // Prefer number-token matches when prompt contains explicit digits
          bestMatch = tokenMatchFromPrompt;
        }

        if (bestMatch) {
          result.matchedStudent = bestMatch.student;
          result.confidence = bestMatch.confidence;
          result.studentName = bestMatch.student.StudentName; // Use the exact name from student list
          try {
            console.log("[aiCommandParser] Student match", {
              command: prompt,
              parsedName: parsed?.studentName,
              finalName: result.studentName,
              matchedField: bestMatch.matchedField,
            });
          } catch (logErr) {
            /* ignore */
          }
        } else {
          console.warn("[aiCommandParser] No student match found", {
            command: prompt,
            parsedName: parsed?.studentName,
          });
        }
      }

      return result;
    }
  } catch (error) {
    console.error("AI parsing error:", error);
    return null;
  }
}

// Enhanced function with validation and confirmation
export async function parseAttendanceCommandWithValidation(
  commandText,
  studentList = [],
  slug = null,
) {
  const result = await parseAttendanceCommandWithAI(
    commandText,
    studentList,
    slug,
  );
  
  if (!result || !result.type) {
    return {
      success: false,
      error: "Command not recognized",
      originalCommand: commandText,
      suggestions: [
        "Try: 'Mark John present'",
        "Try: 'Mark all absent'", 
        "Try: 'Present except Maria'"
      ]
    };
  }

  // Validate student name if required for single student commands
  const singleStudentCommands = ['present', 'absent', 'late'];
  if (singleStudentCommands.includes(result.type) && !result.matchedStudent) {
    return {
      success: false,
      error: `Student "${result.studentName}" not found`,
      originalCommand: commandText,
      parsedResult: result,
      suggestions: studentList.map(s => `Try: "Mark ${s.StudentName} ${result.type}"`).slice(0, 5)
    };
  }

  return {
    success: true,
    parsedResult: result,
    confirmationMessage: generateConfirmationMessage(result)
  };
}

function generateConfirmationMessage(result) {
  const { type, studentName, matchedStudent, lessonName } = result;
  
  switch (type) {
    case 'presentAll':
      return "Mark all students as present?";
    case 'absentAll':
      return "Mark all students as absent?";
    case 'lateAll':
      return "Mark all students as late?";
    case 'presentExcept':
      return `Mark all students present except ${studentName}?`;
    case 'absentExcept':
      return `Mark all students absent except ${studentName}?`;
    case 'lateExcept':
      return `Mark all students late except ${studentName}?`;
    case 'navigateLesson':
    case 'goToLesson':
      return `Navigate to ${lessonName} lesson?`;
    case 'nextLesson':
      return "Navigate to next lesson?";
    case 'previousLesson':
      return "Navigate to previous lesson?";
    default:
      if (matchedStudent) {
        return `Mark ${matchedStudent.StudentName} as ${type}?`;
      }
      return `Mark ${studentName} as ${type}?`;
  }
}

export default parseAttendanceCommandWithAI;

